import { message } from 'antd';
import { ContainerErrorInfo } from '../types/container';

/**
 * Error types for classification
 */
export type ErrorType = 'network' | 'business' | 'system' | 'permission' | 'validation';

/**
 * Standardized error information interface
 */
export interface ErrorInfo {
  type: ErrorType;
  message: string;
  code?: string;
  traceId?: string;
  details?: any;
}

/**
 * Network error codes
 */
export const NETWORK_ERROR_CODES = {
  TIMEOUT: 'NETWORK_TIMEOUT',
  CONNECTION_ERROR: 'NETWORK_ERROR',
  OFFLINE: 'NETWORK_OFFLINE',
} as const;

/**
 * Business error codes
 */
export const BUSINESS_ERROR_CODES = {
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  DUPLICATE_CODE: 'DUPLICATE_CODE',
  RESOURCE_NOT_FOUND: 'RESOURCE_NOT_FOUND',
  OPERATION_FAILED: 'OPERATION_FAILED',
} as const;

/**
 * System error codes
 */
export const SYSTEM_ERROR_CODES = {
  SERVER_ERROR: 'SERVER_ERROR',
  UNKNOWN_ERROR: 'UNKNOWN_ERROR',
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
} as const;

/**
 * Permission error codes
 */
export const PERMISSION_ERROR_CODES = {
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  TOKEN_EXPIRED: 'TOKEN_EXPIRED',
} as const;

/**
 * Unified error handler that classifies and transforms errors
 * @param error The error object from API calls or other sources
 * @returns Standardized error information
 */
export function handleError(error: any): ErrorInfo {
  // Network timeout errors
  if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
    return {
      type: 'network',
      message: '网络连接超时，请检查网络后重试',
      code: NETWORK_ERROR_CODES.TIMEOUT,
    };
  }

  // Network connection errors
  if (error.code === 'NETWORK_ERROR' || !error.response) {
    return {
      type: 'network',
      message: '网络连接异常，请检查网络后重试',
      code: NETWORK_ERROR_CODES.CONNECTION_ERROR,
    };
  }

  // Check if user is offline
  if (!navigator.onLine) {
    return {
      type: 'network',
      message: '网络连接已断开，请检查网络连接',
      code: NETWORK_ERROR_CODES.OFFLINE,
    };
  }

  // HTTP status code based errors
  if (error.response?.status) {
    const status = error.response.status;
    
    // Authentication errors
    if (status === 401) {
      return {
        type: 'permission',
        message: '登录已过期，请重新登录',
        code: PERMISSION_ERROR_CODES.UNAUTHORIZED,
      };
    }

    // Authorization errors
    if (status === 403) {
      return {
        type: 'permission',
        message: '权限不足，请联系管理员',
        code: PERMISSION_ERROR_CODES.FORBIDDEN,
      };
    }

    // Resource not found
    if (status === 404) {
      return {
        type: 'business',
        message: '请求的资源不存在',
        code: BUSINESS_ERROR_CODES.RESOURCE_NOT_FOUND,
      };
    }

    // Server errors
    if (status >= 500) {
      return {
        type: 'system',
        message: '服务器内部错误，请稍后重试',
        code: SYSTEM_ERROR_CODES.SERVER_ERROR,
      };
    }
  }

  // Business logic errors from API response
  if (error.response?.data?.success === false) {
    const errorData = error.response.data;
    return {
      type: 'business',
      message: errorData.errorMessage || errorData.message || '操作失败',
      code: errorData.errorCode || errorData.code || BUSINESS_ERROR_CODES.OPERATION_FAILED,
      traceId: errorData.traceId,
      details: errorData,
    };
  }

  // Validation errors
  if (error.name === 'ValidationError' || error.type === 'validation') {
    return {
      type: 'validation',
      message: error.message || '数据验证失败',
      code: BUSINESS_ERROR_CODES.VALIDATION_FAILED,
      details: error.details,
    };
  }

  // Default unknown error
  return {
    type: 'system',
    message: error.message || '系统异常，请稍后重试',
    code: SYSTEM_ERROR_CODES.UNKNOWN_ERROR,
    details: error,
  };
}

export const createErrorInfo = (error: any, defaultMessage: string): ErrorInfo => {
  return {
    type: getErrorType(error),
    message: getErrorMessage(error, defaultMessage),
    code: error?.errorCode || error?.code,
    traceId: error?.traceId
  };
};

// Error type mapping
export const getErrorType = (error: any): ErrorInfo['type'] => {
  if (!error) return 'server';

  if (error.code === 'NETWORK_ERROR' || error.message?.includes('Network')) {
    return 'network';
  }

  if (error.status === 403 || error.message?.includes('permission')) {
    return 'permission';
  }

  if (error.status === 404 || error.message?.includes('not found')) {
    return 'notFound';
  }

  if (error.status >= 400 && error.status < 500) {
    return 'validation';
  }

  return 'server';
};

// Error message mapping
export const getErrorMessage = (error: any, defaultMessage: string): string => {
  if (error?.errorMessage) {
    return error.errorMessage;
  }

  if (error?.message) {
    return error.message;
  }

  const errorType = getErrorType(error);

  switch (errorType) {
    case 'network':
      return '网络连接异常，请检查网络后重试';
    case 'permission':
      return '您没有权限访问此功能，请联系管理员';
    case 'notFound':
      return '请求的资源不存在';
    case 'validation':
      return '请求参数有误，请检查后重试';
    default:
      return defaultMessage || '系统繁忙，请稍后再试';
  }
};

export const isRetryableError = (error: any): boolean => {
  const errorType = getErrorType(error);
  return errorType === 'network' || errorType === 'server';
};

// Sanitize error for user display (remove sensitive information)
export const sanitizeErrorForDisplay = (error: any): string => {
  const errorInfo = createErrorInfo(error, '操作失败');

  // Don't show technical details to users
  if (errorInfo.type === 'server' && errorInfo.message.includes('500')) {
    return '系统繁忙，请稍后再试';
  }

  return errorInfo.message;
};

/**
 * Show error message using Ant Design message component
 * @param error Error information or error object
 * @param duration Duration to show the message (default: 4.5s)
 */
export function showErrorMessage(error: ErrorInfo | any, duration: number = 4.5): void {
  const errorInfo = error.type ? error as ErrorInfo : handleError(error);
  
  let messageContent = errorInfo.message;
  
  // Add trace ID for business errors if available
  if (errorInfo.type === 'business' && errorInfo.traceId) {
    messageContent += ` (追踪ID: ${errorInfo.traceId})`;
  }
  
  message.error(messageContent, duration);
}

/**
 * Show success message using Ant Design message component
 * @param content Success message content
 * @param duration Duration to show the message (default: 3s)
 */
export function showSuccessMessage(content: string, duration: number = 3): void {
  message.success(content, duration);
}

/**
 * Show warning message using Ant Design message component
 * @param content Warning message content
 * @param duration Duration to show the message (default: 4s)
 */
export function showWarningMessage(content: string, duration: number = 4): void {
  message.warning(content, duration);
}

/**
 * Show info message using Ant Design message component
 * @param content Info message content
 * @param duration Duration to show the message (default: 3s)
 */
export function showInfoMessage(content: string, duration: number = 3): void {
  message.info(content, duration);
}

/**
 * Get user-friendly error message based on error type and code
 * @param errorInfo Error information
 * @returns User-friendly error message
 */
export function getUserFriendlyErrorMessage(errorInfo: ErrorInfo): string {
  switch (errorInfo.type) {
    case 'network':
      switch (errorInfo.code) {
        case NETWORK_ERROR_CODES.TIMEOUT:
          return '网络请求超时，请检查网络连接后重试';
        case NETWORK_ERROR_CODES.OFFLINE:
          return '网络连接已断开，请检查网络设置';
        default:
          return '网络连接异常，请稍后重试';
      }
    
    case 'permission':
      switch (errorInfo.code) {
        case PERMISSION_ERROR_CODES.UNAUTHORIZED:
          return '登录状态已过期，请重新登录';
        case PERMISSION_ERROR_CODES.FORBIDDEN:
          return '您没有执行此操作的权限，请联系管理员';
        default:
          return '权限验证失败，请重新登录';
      }
    
    case 'business':
      switch (errorInfo.code) {
        case BUSINESS_ERROR_CODES.DUPLICATE_CODE:
          return '托盘编码已存在，请使用其他编码';
        case BUSINESS_ERROR_CODES.RESOURCE_NOT_FOUND:
          return '请求的托盘不存在或已被删除';
        case BUSINESS_ERROR_CODES.VALIDATION_FAILED:
          return '数据验证失败，请检查输入信息';
        default:
          return errorInfo.message || '操作失败，请稍后重试';
      }
    
    case 'validation':
      return errorInfo.message || '输入数据格式不正确，请检查后重试';
    
    case 'system':
      switch (errorInfo.code) {
        case SYSTEM_ERROR_CODES.SERVER_ERROR:
          return '服务器暂时无法处理请求，请稍后重试';
        case SYSTEM_ERROR_CODES.SERVICE_UNAVAILABLE:
          return '服务暂时不可用，请稍后重试';
        default:
          return '系统异常，请稍后重试或联系技术支持';
      }
    
    default:
      return errorInfo.message || '未知错误，请稍后重试';
  }
}

/**
 * Log error for debugging and monitoring
 * @param error Error information
 * @param context Additional context information
 */
export function logError(error: ErrorInfo, context?: any): void {
  const logData = {
    timestamp: new Date().toISOString(),
    error,
    context,
    userAgent: navigator.userAgent,
    url: window.location.href,
  };
  
  console.error('Error logged:', logData);
  
  // In production, you might want to send this to a logging service
  // Example: sendToLoggingService(logData);
}

/**
 * Create a retry function for failed operations
 * @param operation The operation to retry
 * @param maxRetries Maximum number of retries (default: 3)
 * @param delay Delay between retries in milliseconds (default: 1000)
 * @returns Promise that resolves when operation succeeds or max retries reached
 */
export async function retryOperation<T>(
  operation: () => Promise<T>,
  maxRetries: number = 3,
  delay: number = 1000
): Promise<T> {
  let lastError: any;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;
      const errorInfo = handleError(error);
      
      // Don't retry for certain error types
      if (errorInfo.type === 'permission' || errorInfo.type === 'validation') {
        throw error;
      }
      
      // If this is the last attempt, throw the error
      if (attempt === maxRetries) {
        throw error;
      }
      
      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, delay * attempt));
    }
  }
  
  throw lastError;
}

/**
 * Convert ContainerErrorInfo to ErrorInfo for compatibility
 * @param containerError Container-specific error info
 * @returns Standardized error info
 */
export function convertContainerError(containerError: ContainerErrorInfo): ErrorInfo {
  return {
    type: containerError.type as ErrorType,
    message: containerError.message,
    code: containerError.code,
    traceId: containerError.traceId,
  };
}